/** Fase 4 — painel de decisão da equipe: indicadores, alertas explicáveis e tabela coletiva com filtros. */
import { useMemo, useState } from "react";
import { api } from "../../api/client";
import type { Coach, CoachDashboard as Dash, CoachRow, Group } from "../../api/types";
import { useAuth } from "../../auth/AuthContext";
import { AlertsList, Kpi, rate } from "../../components/performance";
import { Badge, Button, ErrorState, Loading, SelectField, TextField } from "../../components/ui";
import { formatDate, formatDuration, metersToKm } from "../../lib/format";
import { addDays, iso } from "../../lib/training";
import { useAsync } from "../../lib/useAsync";
import { Link } from "../../router/router";
import { t } from "../../i18n";

const PRESETS = [
  { days: 7, label: t("7 dias") },
  { days: 28, label: t("4 semanas") },
  { days: 90, label: t("90 dias") },
];

type SortKey = "name" | "completion" | "done" | "last" | "alerts";

function daysSince(isoDate: string | null, today: string): number | null {
  if (!isoDate) return null;
  return Math.round((Date.parse(today) - Date.parse(isoDate)) / 86_400_000);
}

export function CoachDashboard({ orgId }: { orgId: string }) {
  const { can } = useAuth();
  const isManager = can("org.update");
  const [days, setDays] = useState(28);
  const [coach, setCoach] = useState("");
  const [group, setGroup] = useState("");
  const [status, setStatus] = useState("active");
  const [q, setQ] = useState("");
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<SortKey>("alerts");
  const [allAlerts, setAllAlerts] = useState(false);
  const to = iso(new Date());
  const from = iso(addDays(new Date(), -(days - 1)));
  const coaches = useAsync(() => (isManager ? api.get<Coach[]>(`/orgs/${orgId}/coaches`) : Promise.resolve([] as Coach[])), [orgId, isManager]);
  const groups = useAsync(() => api.get<Group[]>(`/orgs/${orgId}/groups`), [orgId]);
  const dash = useAsync(
    () => api.get<Dash>(`/orgs/${orgId}/dashboard`, { from, to, coach_id: coach || undefined, group_id: group || undefined, status, q: query || undefined }),
    [orgId, from, coach, group, status, query],
  );
  const d = dash.data;
  const rows = useMemo(() => {
    const list = [...(d?.rows ?? [])];
    const cmp: Record<SortKey, (a: CoachRow, b: CoachRow) => number> = {
      name: (a, b) => a.athlete_name.localeCompare(b.athlete_name),
      completion: (a, b) => (a.completion_rate ?? 2) - (b.completion_rate ?? 2),
      done: (a, b) => b.done_distance_m - a.done_distance_m,
      last: (a, b) => (a.last_activity_date ?? "").localeCompare(b.last_activity_date ?? ""),
      alerts: (a, b) => b.alerts.length - a.alerts.length || a.athlete_name.localeCompare(b.athlete_name),
    };
    return list.sort(cmp[sort]);
  }, [d, sort]);
  const base = `/org/${orgId}`;
  const alerts = d?.alerts ?? [];
  return (
    <section aria-labelledby="painel">
      <h2 id="painel" className="sr-only">
        {t("Painel de acompanhamento")}
      </h2>
      <div className="filters" role="group" aria-label={t("Filtros do painel")}>
        <div>
          <span className="label-sm">{t("Período")}</span>
          <div className="segmented">
            {PRESETS.map((p) => (
              <button key={p.days} type="button" aria-pressed={days === p.days} onClick={() => setDays(p.days)}>
                {p.label}
              </button>
            ))}
          </div>
        </div>
        {isManager && (
          <SelectField label={t("Treinador")} value={coach} onChange={(e) => setCoach(e.target.value)}>
            <option value="">{t("Todos")}</option>
            {(coaches.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.full_name}
              </option>
            ))}
          </SelectField>
        )}
        <SelectField label={t("Grupo")} value={group} onChange={(e) => setGroup(e.target.value)}>
          <option value="">{t("Todos")}</option>
          {(groups.data ?? []).map((g) => (
            <option key={g.id} value={g.id}>
              {g.name}
            </option>
          ))}
        </SelectField>
        <SelectField label={t("Situação")} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="active">{t("Ativos")}</option>
          <option value="paused">{t("Pausados")}</option>
          <option value="inactive">{t("Inativos")}</option>
        </SelectField>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setQuery(q.trim());
          }}
          style={{ display: "flex", gap: 8, alignItems: "flex-end" }}
        >
          <TextField label={t("Buscar atleta")} value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Nome")} />
          <Button type="submit">{t("Buscar")}</Button>
        </form>
      </div>

      {dash.error && <ErrorState error={dash.error} onRetry={dash.reload} />}
      {!d && dash.loading && <Loading />}
      {d && (
        <div className={dash.loading ? "refetching" : undefined}>
          <dl className="kpis">
            <Kpi label={t("Atletas no filtro")} value={String(d.indicators.athletes)} sub={t("{a} a {b}", { a: formatDate(d.period.start), b: formatDate(d.period.end) })} />
            <Kpi
              label={t("Treinos concluídos")}
              value={d.indicators.completion_rate != null ? rate(d.indicators.completion_rate) : "—"}
              sub={t("{workouts_done} de {workouts_due} prescritos até hoje", { workouts_done: d.indicators.workouts_done, workouts_due: d.indicators.workouts_due })}
              meter={d.indicators.completion_rate}
            />
            <Kpi label={t("Sem atividade há 7+ dias")} value={String(d.indicators.athletes_without_recent_activity)} />
            <Kpi label={t("Queda de frequência")} value={String(d.indicators.athletes_frequency_drop)} />
            <Kpi label={t("Sem treino publicado (7 dias)")} value={String(d.indicators.athletes_without_plan_ahead)} />
            <Kpi label={t("Atividades sem treino vinculado (7 dias)")} value={String(d.indicators.activities_unmatched_7d)} />
          </dl>

          <section className="section" aria-labelledby="alertas-painel">
            <div className="row-between">
              <h2 id="alertas-painel">
                {t("Alertas abertos")} <span className="muted">({alerts.length})</span>
              </h2>
              {alerts.length > 6 && (
                <Button size="sm" variant="ghost" onClick={() => setAllAlerts((v) => !v)}>
                  {allAlerts ? t("Mostrar menos") : t("Mostrar todos")}
                </Button>
              )}
            </div>
            <div style={{ marginTop: 10 }}>
              <AlertsList
                alerts={allAlerts ? alerts : alerts.slice(0, 6)}
                orgId={orgId}
                showAthlete
                athleteHref={(id) => `${base}/atletas/${id}/evolucao`}
                onChanged={dash.reload}
              />
            </div>
          </section>

          <section className="section" aria-labelledby="coletivo">
            <div className="row-between">
              <h2 id="coletivo">{t("Acompanhamento coletivo")}</h2>
              <SelectField label={t("Ordenar por")} value={sort} onChange={(e) => setSort(e.target.value as SortKey)}>
                <option value="alerts">{t("Mais alertas")}</option>
                <option value="completion">{t("Menor conclusão")}</option>
                <option value="done">{t("Maior volume")}</option>
                <option value="last">{t("Última atividade mais antiga")}</option>
                <option value="name">{t("Nome")}</option>
              </SelectField>
            </div>
            {rows.length === 0 ? (
              <p className="muted" style={{ marginTop: 10 }}>
                {t("Nenhum atleta no filtro.")}
              </p>
            ) : (
              <div className="table-wrap" style={{ marginTop: 10 }}>
                <table className="data responsive coach-table">
                  <thead>
                    <tr>
                      <th>{t("Atleta")}</th>
                      <th>{t("Treinador")}</th>
                      <th className="right">{t("Planejado")}</th>
                      <th className="right">{t("Realizado")}</th>
                      <th className="right">{t("Conclusão")}</th>
                      <th className="right">{t("Sessões")}</th>
                      <th>{t("Última atividade")}</th>
                      <th className="right">{t("Evolução")}</th>
                      <th className="right">{t("RPE médio")}</th>
                      <th>{t("Alertas")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const since = daysSince(r.last_activity_date, d.today);
                      return (
                        <tr key={r.athlete_id}>
                          <td className="nowrap">
                            <Link className="row-link" to={`${base}/atletas/${r.athlete_id}/evolucao`}>
                              {r.athlete_name}
                            </Link>
                            {r.group_name && <div className="sub">{r.group_name}</div>}
                          </td>
                          <td data-label={t("Treinador")}>{r.coach_name ?? "—"}</td>
                          <td data-label={t("Planejado")} className="right num">{r.planned_distance_m ? metersToKm(r.planned_distance_m) : "—"}</td>
                          <td data-label={t("Realizado")} className="right num">{metersToKm(r.done_distance_m)}</td>
                          <td data-label={t("Conclusão")} className="right num">
                            {r.completion_rate != null ? `${rate(r.completion_rate)} (${r.planned_done}/${r.planned_due})` : "—"}
                          </td>
                          <td data-label={t("Sessões")} className="right num">{r.sessions}</td>
                          <td data-label={t("Última atividade")}>
                            {r.last_activity_date ? `${formatDate(r.last_activity_date)}${since != null ? ` (${since === 0 ? "hoje" : t("há {since} d", { since })})` : ""}` : "—"}
                          </td>
                          <td data-label={t("Evolução")} className={`right num ${r.trend_pct != null ? (r.trend_pct >= 0 ? "trend-up" : "trend-down") : ""}`} title={t("Volume da 2ª metade do período comparado à 1ª")}>
                            {r.trend_pct != null ? `${r.trend_pct >= 0 ? "▲ +" : "▼ "}${Math.round(r.trend_pct * 100)} %` : "—"}
                          </td>
                          <td data-label={t("RPE médio")} className="right num" title={r.rpe_coverage != null ? t("RPE em {pct} % das corridas", { pct: Math.round(r.rpe_coverage * 100) }) : undefined}>
                            {r.avg_rpe ?? "—"}
                          </td>
                          <td data-label={t("Alertas")}>
                            <span className="alert-chips">
                              {r.alerts.length === 0 ? (
                                <span className="muted">—</span>
                              ) : (
                                r.alerts.map((a) => (
                                  <Badge key={a.key} tone={a.severity === "attention" ? "warn" : "off"}>
                                    {a.title}
                                  </Badge>
                                ))
                              )}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            <p className="muted" style={{ fontSize: "var(--step--1)", marginTop: 8 }}>
              {t("Evolução: volume da segunda metade do período comparado à primeira. Conclusão: treinos publicados até hoje com atividade vinculada ou marcados como feitos.")}
            </p>
          </section>

          <div className="chart-grid section">
            <section aria-labelledby="provas-equipe">
              <h2 id="provas-equipe">{t("Próximas provas (60 dias)")}</h2>
              <div className="panel" style={{ marginTop: 10 }}>
                {d.upcoming_races.length === 0 ? (
                  <p className="panel-body muted">{t("Nenhuma prova cadastrada.")}</p>
                ) : (
                  <ul className="list-plain">
                    {d.upcoming_races.map((r) => (
                      <li key={r.id} className="row-between">
                        <span>
                          <span className={`prio ${r.priority}`}>{r.priority}</span> <strong>{r.name}</strong>
                          <div className="sub">
                            <Link to={`${base}/atletas/${r.athlete_id}/evolucao`}>{r.athlete_name}</Link> · {metersToKm(r.distance_m)}
                            {r.target_time_s && ` · objetivo ${formatDuration(r.target_time_s)}`}
                          </div>
                        </span>
                        <span className="figs">{formatDate(r.race_date)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </section>
            <section aria-labelledby="distribuicao">
              <h2 id="distribuicao">{t("Atletas por grupo e treinador")}</h2>
              <div className="panel" style={{ marginTop: 10 }}>
                <ul className="list-plain">
                  {d.by_group.map((g) => (
                    <li key={`g${g.name}`} className="row-between">
                      <span>{g.name}</span>
                      <span className="figs">{g.count}</span>
                    </li>
                  ))}
                  {isManager &&
                    d.by_coach.map((c) => (
                      <li key={`c${c.name}`} className="row-between">
                        <span className="muted">{t("Treinador: {name}", { name: c.name })}</span>
                        <span className="figs">{c.count}</span>
                      </li>
                    ))}
                </ul>
              </div>
            </section>
          </div>
        </div>
      )}
    </section>
  );
}
